import { apiService } from './api';
import { User, AuthResponse } from './authService';

export interface AdminLoginRequest {
  password: string;
}

export interface AdminAuthResponse {
  message: string;
  token: string;
  isAdmin: boolean;
}

export interface MSP {
  id: number;
  company: string;
  contactName: string;
  phone?: string;
  email: string;
  logoUrl?: string;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
  inviteCodes?: InviteCode[];
  users?: User[];
  userCount?: number;
  activeInviteCodes?: number;
  totalInvitesUsed?: number;
}

export interface InviteCode {
  id: number;
  code: string;
  description?: string;
  usageLimit: number;
  usageCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  invitesLeft?: number;
  canBeUsed?: boolean;
}

export interface PaginatedUsersResponse {
  users: User[];
  total: number;
  page: number;
  totalPages: number;
}

export const adminService = {
  async login(credentials: AdminLoginRequest): Promise<AdminAuthResponse> {
    const { data } = await apiService.post<AdminAuthResponse>('/admin/login', credentials);
    
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('isAdmin', 'true');
    
    return data;
  },

  async getUsers(params?: { search?: string; page?: number; limit?: number }): Promise<PaginatedUsersResponse> {
    const { data } = await apiService.get<PaginatedUsersResponse>('/admin/users', { params });
    return data;
  },

  async deleteUser(userId: number): Promise<{ message: string }> {
    const { data } = await apiService.delete<{ message: string }>(`/admin/users/${userId}`);
    return data;
  },

  async updateUser(userId: number, userData: { firstName?: string; lastName?: string; companyName?: string; isActive?: boolean; isVerified?: boolean; mspId?: number | null; inviteCodeUsed?: string }): Promise<{ user: User }> {
    const { data } = await apiService.put<{ user: User }>(`/admin/users/${userId}`, userData);
    return data;
  },

  async loginAsUser(userId: number): Promise<AuthResponse> {
    const { data } = await apiService.post<AuthResponse>(`/admin/users/${userId}/login`);
    
    // Store admin token before switching
    const adminToken = localStorage.getItem('authToken');
    if (adminToken) {
      localStorage.setItem('adminToken', adminToken);
      localStorage.setItem('isImpersonating', 'true');
    }
    
    // Clear admin state
    localStorage.removeItem('isAdmin');
    
    // Set user tokens and data
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    return data;
  },

  // MSP methods
  async getMSPs(params?: { search?: string; page?: number; limit?: number }): Promise<{ msps: MSP[]; pagination: any }> {
    const { data } = await apiService.get<{ msps: MSP[]; pagination: any }>('/admin/msps', { params });
    return data;
  },

  async createMSP(formData: FormData): Promise<{ msp: MSP }> {
    const { data } = await apiService.post<{ msp: MSP }>('/admin/msps', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  async updateMSP(id: number, formData: FormData): Promise<{ msp: MSP }> {
    const { data } = await apiService.put<{ msp: MSP }>(`/admin/msps/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  async deleteMSP(id: number): Promise<{ message: string }> {
    const { data } = await apiService.delete<{ message: string }>(`/admin/msps/${id}`);
    return data;
  },


  // Invite Code methods
  async getInviteCodes(params?: { search?: string; isActive?: boolean; page?: number; limit?: number }): Promise<{ inviteCodes: InviteCode[]; pagination: any }> {
    const { data } = await apiService.get<{ inviteCodes: InviteCode[]; pagination: any }>('/admin/invite-codes', { params });
    return data;
  },

  async createInviteCode(inviteCode: { code: string; description?: string; usageLimit: number }): Promise<{ inviteCode: InviteCode }> {
    const { data } = await apiService.post<{ inviteCode: InviteCode }>('/admin/invite-codes', inviteCode);
    return data;
  },

  async updateInviteCode(id: number, inviteCode: Partial<{ description?: string; usageLimit?: number; isActive?: boolean }>): Promise<{ inviteCode: InviteCode }> {
    const { data } = await apiService.put<{ inviteCode: InviteCode }>(`/admin/invite-codes/${id}`, inviteCode);
    return data;
  },

  async deleteInviteCode(id: number): Promise<{ message: string }> {
    const { data } = await apiService.delete<{ message: string }>(`/admin/invite-codes/${id}`);
    return data;
  },

  async validateInviteCode(code: string): Promise<{ valid: boolean; msp?: any; invitesLeft?: number }> {
    const { data } = await apiService.get<{ valid: boolean; msp?: any; invitesLeft?: number }>(`/invite-codes/validate/${code}`);
    return data;
  },

  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('isImpersonating');
  },

  isAdmin(): boolean {
    return localStorage.getItem('isAdmin') === 'true';
  },

  isImpersonating(): boolean {
    return localStorage.getItem('isImpersonating') === 'true';
  },

  async returnToAdmin(): Promise<void> {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      throw new Error('No admin token found');
    }

    // Clear impersonation state
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('adminToken');
    
    // Clear user state
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    
    // Restore admin state
    localStorage.setItem('authToken', adminToken);
    localStorage.setItem('isAdmin', 'true');
    
    // Redirect to admin dashboard
    window.location.href = '/admin';
  }
};

export default adminService;